/**
 * Alternatives
 */
const NON_DIGITS_REGEX = /\D/g

const DietaryRawMaterialAlternativeDetailsToModel = (
	json: IDietaryRawMaterialAlternativeDetails,
): IDietaryRawMaterialAlternativeDetailsModel => {
	const { id, name_trans, internal_name, warehouse_name } = json

	return {
		id,
		name: name_trans,
		internalName: internal_name,
		warehouseName: warehouse_name,
	}
}

export const DietaryRawMaterialAlternativeToModel = (
	json: IDietaryRawMaterialAlternative,
): IDietaryRawMaterialAlternativeModel => {
	const { order, auto_sync, net_weight, alternative_raw_material } = json

	return {
		order,
		autoSync: auto_sync,
		netWeight: net_weight,
		alternativeRawMaterial: DietaryRawMaterialAlternativeDetailsToModel(alternative_raw_material),
	}
}

export const DietaryRawMaterialModelToAlternativeRawMaterialDetails = (
	model: IDietMaterialModel,
): IDietaryRawMaterialAlternativeDetailsModel => {
	const { id, name, internalName, warehouseName } = model

	return {
		id,
		internalName,
		warehouseName,
		name: name.pl,
	}
}

const DietaryRawMaterialAlternativeToPayload = (
	model: IDietaryRawMaterialAlternativeModel,
): IDietaryRawMaterialAlternativePayload => {
	const { autoSync, netWeight, order, alternativeRawMaterial } = model

	if (!alternativeRawMaterial?.id) {
		throw new Error('Alternative raw material is required')
	}

	return {
		id: alternativeRawMaterial?.id ?? null,
		auto_sync: autoSync,
		order: order ?? null,
		net_weight: netWeight,
	}
}

/**
 * Nutritional parameters
 */
const DietaryRawMaterialNutritionalParametersToPayload = (
	form: IDietaryRawMaterialForm,
): IDietaryRawMaterialNutritionalParameterPayload[] => {
	const array: IDietaryRawMaterialNutritionalParameterPayload[] = []

	Object.entries(form).forEach(([name, value]) => {
		if (!name.startsWith('nutritionalParameter')) return

		const id = Number(name.replace(NON_DIGITS_REGEX, ''))

		let adjustedValue: number | null | undefined
		if (value === null) {
			adjustedValue = undefined
		} else {
			let parsedValue = value
			if (typeof parsedValue === 'string' && parsedValue.includes(',')) {
				parsedValue = parsedValue.replace(',', '.')
			}
			const numValue = Number(parsedValue)
			if (Number.isNaN(numValue)) {
				adjustedValue = null
			} else if (numValue === 0) {
				adjustedValue = 0
			} else {
				adjustedValue = numValue
			}
		}

		if (adjustedValue === null || adjustedValue === undefined) return

		array.push({ id, value: adjustedValue })
	})

	return array
}

/**
 * Payloads
 */
const DietaryRawMaterialToPayload = (
	form: IDietaryRawMaterialForm,
	mapper: IDietaryRawMaterialPayloadMapper,
): IDietaryRawMaterialPayload => {
	const {
		internalName,
		warehouseName,
		foodCategory,
		unit,
		ean,
		externalId,
		mapiPieceWeight,
		isLiquid,
		isMonoproduct,
		supplierName,
		thermalProcessingCoefficient,
		mechanicalProcessingCoefficient,
		ingredients,
	} = form

	const { allergens, dietaryTags, isIngredientsManual, alternatives } = mapper

	return {
		unit,
		ean,
		internal_name: internalName,
		warehouse_name: warehouseName,
		thermal_processing_coefficient: thermalProcessingCoefficient,
		mechanical_processing_coefficient: mechanicalProcessingCoefficient,
		is_active: true,
		external_id: externalId,
		mapi_piece_weight: mapiPieceWeight,
		is_liquid: isLiquid,
		is_monoproduct: isMonoproduct,
		supplier_name: supplierName ?? null,
		ingredients: isIngredientsManual ? ingredients : null,
		food_category: foodCategory,
		allergens: allergens ? allergens.map(allergen => allergen.value) : [],
		dietary_tags: dietaryTags ? dietaryTags.map(tag => tag.value) : [],
		nutritional_values: DietaryRawMaterialNutritionalParametersToPayload(form),
		alternatives: alternatives ? alternatives.map(DietaryRawMaterialAlternativeToPayload) : [],
	}
}

export const DietaryRawMaterialToCreatePayload = (
	form: IDietaryRawMaterialForm,
	mapper: IDietaryRawMaterialPayloadMapper,
): IDietaryRawMaterialCreatePayload => {
	const payload = DietaryRawMaterialToPayload(form, mapper)

	const name = LangContentToCreatePayload(form)

	return {
		name,
		...payload,
	}
}

export const DietaryRawMaterialToUpdatePayload = (
	model: IDietMaterialModel,
	form: IDietaryRawMaterialForm,
	mapper: IDietaryRawMaterialPayloadMapper,
): IDietaryRawMaterialUpdatePayload => {
	const { id } = model

	const payload = DietaryRawMaterialToPayload(form, mapper)

	const name = LangContentToUpdatePayload(form, model)

	return {
		id,
		name,
		...payload,
	}
}
